/**
 * Wire shapes of the JSON API, shared by the server tests and the browser client. Timestamps are
 * ISO strings (JSON has no Date), calendar days are `YYYY-MM-DD` strings, ids are bare uuids.
 * Changing a route means changing its schema here; `contracts.test.ts` parses live route output.
 */
import { z } from "zod";
import { normalizeProtectedHours } from "../btb/protected-hours";

export const modeSchema = z.enum(["shadow", "live"]);
export const roleSchema = z.enum(["admin", "owner", "none"]);
export const edgeSchema = z.enum(["start", "end"]);
export const actionTypeSchema = z.enum(["create", "remove", "resize", "release", "lock", "adopt", "would_create", "would_remove", "would_resize", "would_release", "would_lock", "would_adopt", "skip"]);
export const plannedTypeSchema = z.enum(["create", "remove", "resize", "release", "lock", "adopt", "skip"]);
export const jobStatusSchema = z.enum(["queued", "running", "done", "failed"]);
export const ruleScopeSchema = z.enum(["global", "owner", "location"]);
const iso = z.string();
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const meSchema = z.object({
  email: z.string(), name: z.string().nullable(), role: roleSchema, superAdmin: z.boolean(), ownerIds: z.array(z.string()), csrfToken: z.string(), mode: modeSchema, globalPaused: z.boolean(),
  /** Projected from NIGHTLY_REPLAN_UTC_HOUR; the cron itself runs in Railway. */
  nextNightlyRunAt: iso,
  /** True only for an owner session while the admin tracking switch is on; the web tracker stays off otherwise. */
  usageTracking: z.boolean().default(false),
});

export const apiErrorSchema = z.object({
  error: z.string(), message: z.string().optional(), ref: z.string().optional(), email: z.string().optional(),
  errors: z.array(z.string()).optional(), issues: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
});

export const locationSummarySchema = z.object({
  blvdId: z.string(), name: z.string(), ownerIdentifier: z.string(), timezone: z.string(), state: z.string().nullable(),
  enabled: z.boolean(), paused: z.boolean(), windingDown: z.boolean(), activeBlocks: z.number(), ruleScope: ruleScopeSchema, lookaheadDays: z.number(),
  weekCounts: z.object({ created: z.number(), removed: z.number(), wouldCreate: z.number(), wouldRemove: z.number() }),
  /** Latest finished planning job (replan_location_day, evaluate, backfill) for this location; null until one runs. */
  lastPlannedAt: iso.nullable(),
  /** Latest Boulevard webhook delivery for this location; null until one arrives. */
  lastEventAt: iso.nullable(),
});
export const locationsResponseSchema = z.object({ locations: z.array(locationSummarySchema) });
export const enabledResponseSchema = z.object({ enabled: z.boolean(), backfillJobId: z.string().nullable(), webhookJobId: z.string().nullable() });
export const pausedResponseSchema = z.object({ paused: z.boolean() });
export const enqueuedResponseSchema = z.object({ jobId: z.string(), coalesced: z.boolean().optional() });

export const tierSchema = z.object({ minGap: z.number(), maxGap: z.number().nullable(), blockMinutes: z.number() });
/** Wire form of `ProtectedHours`; the transform fills any weekday a partial payload leaves out. */
const protectedHoursSchema = z
  .record(z.string(), z.object({ btb: z.boolean(), protectFrom: z.string().nullable(), protectTo: z.string().nullable() }))
  .transform(normalizeProtectedHours);
export const ruleSetSchema = z.object({ tiers: z.array(tierSchema), minShiftMinutes: z.number(), removalUtilizationPct: z.number(), removalMinGapMinutes: z.number(), turnoverMaxMinutes: z.number(), lookaheadDays: z.number(), startLockHoursBefore: z.number().nullable(), endLockHoursBefore: z.number().nullable(), protectedHours: protectedHoursSchema, aiBookingEnabled: z.boolean(), adoptManualBtb: z.boolean(), keepSomeoneIn: z.boolean() });
export const resolvedRuleSetSchema = z.object({ id: z.string(), scope: ruleScopeSchema, version: z.number(), rules: ruleSetSchema });
export const rulesResponseSchema = z.object({ scope: ruleScopeSchema, key: z.string().nullable(), active: resolvedRuleSetSchema.nullable(), effective: resolvedRuleSetSchema, default: ruleSetSchema });
const dayHoursSchema = z.object({ open: z.string(), close: z.string() });
/** Boulevard opening hours per weekday (Sunday = 0); `source` is "default" when none could be read. */
export const rulesHoursResponseSchema = z.object({
  hours: z.record(z.string(), dayHoursSchema.nullable()).nullable(),
  source: z.enum(["boulevard", "default"]),
});
export const replanSchema = z.object({ locations: z.number(), jobs: z.number() });
/** `replan: null` = the change is saved but its re-plan could not be queued; the nightly pass applies it. */
export const saveRulesResponseSchema = z.object({ saved: resolvedRuleSetSchema, replan: replanSchema.nullable() });
export const clearRulesResponseSchema = z.object({ cleared: z.boolean(), replan: replanSchema.nullable() });

export const actionRowSchema = z.object({
  id: z.string(), blvdId: z.string(), locationName: z.string(), staffId: z.string(), staffName: z.string(), date: day, edge: edgeSchema,
  type: actionTypeSchema, intendedType: actionTypeSchema.nullable(), reason: z.string(), gapMinutes: z.number(), utilizationPct: z.number(),
  tierIndex: z.number().nullable(), mode: modeSchema, jobId: z.string().nullable(), blvdTimeblockId: z.string().nullable(), error: z.string().nullable(), createdAt: iso,
});
export const actionsResponseSchema = z.object({ actions: z.array(actionRowSchema), total: z.number() });

export const jobResponseSchema = z.object({
  id: z.string(), type: z.string(), status: jobStatusSchema, attempts: z.number(), result: z.unknown().nullable(), lastError: z.string().nullable(),
  createdAt: iso, startedAt: iso.nullable(), finishedAt: iso.nullable(),
});
export const previewResultSchema = z.object({
  blvdId: z.string(), from: day, to: day, shifts: z.number(),
  /** True when the preview planned with unsaved rules sent from the Rules page. */
  draftRules: z.boolean().optional(),
  actions: z.array(z.object({ date: day, staffId: z.string(), staffName: z.string(), edge: edgeSchema, type: plannedTypeSchema, minutes: z.number().nullable(), reason: z.string(), gapMinutes: z.number(), utilizationPct: z.number() })),
});
export const smokeTestResultSchema = z.object({ step: z.enum(["create", "remove"]), recordedType: z.string(), blvdTimeblockId: z.string().nullable(), error: z.string().nullable() });

export const adminEventSchema = z.object({ id: z.string(), eventType: z.string(), blvdId: z.string().nullable(), receivedAt: iso, jobId: z.string().nullable() });
export const adminSystemSchema = z.object({
  mode: modeSchema, globalPaused: z.boolean(), pausedBy: z.string().nullable(), pausedAt: iso.nullable(),
  queue: z.object({ queued: z.number(), running: z.number(), failed: z.number(), done24h: z.number() }),
  rateLimiter: z.object({ available: z.number() }), recentEvents: z.array(adminEventSchema), enabledLocations: z.number(),
  usageTracking: z.boolean().default(true),
});
const usageDaysSchema = z.union([z.literal(7), z.literal(30), z.literal(90)]);
export const usageSummarySchema = z.object({
  days: usageDaysSchema, liveOwners: z.number(), liveOwnersSeen: z.number(), liveOwnersNotSeen: z.number(), medianActiveMinutes: z.number().nullable(),
});
export const usageOwnerRowSchema = z.object({
  ownerId: z.string(), names: z.array(z.string()), contactEmails: z.array(z.string()), locations: z.number(), liveLocations: z.number(),
  lastSeenAt: iso.nullable(), daysActive: z.number(), activeMinutes: z.number(), topPages: z.array(z.string()),
  ruleSavesOwn: z.number(), ruleSavesCorporate: z.number(),
});
export const usageOwnersSchema = z.object({ days: usageDaysSchema, owners: z.array(usageOwnerRowSchema) });
export const usageVisitSchema = z.object({ visitId: z.string(), userEmail: z.string(), startedAt: iso, endedAt: iso, pages: z.array(z.string()), activeMinutes: z.number() });
export const usageVisitsSchema = z.object({ visits: z.array(usageVisitSchema) });
export const usageFeaturesSchema = z.object({
  features: z.array(z.object({
    key: z.string(), label: z.string(), ownersOn: z.number(), byOwner: z.number(), byCorporate: z.number(),
    owners: z.array(z.object({ ownerId: z.string(), on: z.number(), of: z.number(), credit: z.enum(["owner", "corporate"]) })),
  })),
  actionOwners: z.record(z.string(), z.number()),
});
export const usagePagesSchema = z.object({
  pages: z.array(z.object({ path: z.string(), visits: z.number(), owners: z.number(), activeMinutes: z.number() })),
  daily: z.array(z.object({ date: z.string(), ownersSeen: z.number() })),
});
export type UsageSummaryResponse = z.infer<typeof usageSummarySchema>;
export type UsageOwnersResponse = z.infer<typeof usageOwnersSchema>;
export type UsageVisitsResponse = z.infer<typeof usageVisitsSchema>;
export type UsageFeaturesResponse = z.infer<typeof usageFeaturesSchema>;
export type UsagePagesResponse = z.infer<typeof usagePagesSchema>;
export const adminHealthSchema = z.object({
  alerts: z.array(z.object({ key: z.string(), message: z.string(), openedAt: iso })),
  locations: z.array(z.object({ blvdId: z.string(), name: z.string(), timezone: z.string(), lastEventAt: iso.nullable(), events24h: z.number() })),
});
export const skipCategorySchema = z.enum(["gate_disabled", "gate_paused", "gate_unmanaged", "protected", "coverage", "conflict", "past", "locked", "duplicate", "other"]);
export const analyticsTotalsSchema = z.object({ events: z.number(), jobs: z.number(), failedJobs: z.number(), writeErrors: z.number(), blocksCreated: z.number(), blocksRemoved: z.number(), blocksResized: z.number(), blockedMinutes: z.number(), skips: z.number(), decisionsChanged: z.number() });
export const analyticsJobBucketSchema = z.enum(["replan_location_day", "evaluate", "nightly_replan", "reconcile_managed_blocks", "directory_sync", "backfill", "preview", "smoke_test", "other"]);
export const analyticsDailyRowSchema = z.object({
  date: z.string(), events: z.number(), jobs: z.record(analyticsJobBucketSchema, z.number()), failedJobs: z.number(),
  replanDurationMs: z.object({ p50: z.number(), p95: z.number() }).nullable(),
  created: z.number(), removed: z.number(), resized: z.number(), createdMinutes: z.number(), writeErrors: z.number(), skips: z.number(), decisionsChanged: z.number(),
});
export const analyticsQueueSampleSchema = z.object({ sampledAt: iso, priorityDue: z.number(), priorityOldestWaitS: z.number().nullable(), backgroundDue: z.number(), backgroundOldestWaitS: z.number().nullable(), running: z.number() });
export const analyticsLocationRowSchema = z.object({
  blvdId: z.string(), name: z.string(), ownerIdentifier: z.string(), timezone: z.string(), enabled: z.boolean(), paused: z.boolean(), windingDown: z.boolean(),
  events: z.number(), jobs: z.number(), failedJobs: z.number(), created: z.number(), removed: z.number(), writeErrors: z.number(), placedMinutes: z.number(), activeBlocks: z.number(), blockedMinutes: z.number(),
});
export const adminAnalyticsSchema = z.object({
  days: z.union([z.literal(7), z.literal(30), z.literal(90)]), from: z.string(), to: z.string(), mode: modeSchema,
  totals: z.object({ current: analyticsTotalsSchema, previous: analyticsTotalsSchema }),
  daily: z.array(analyticsDailyRowSchema), queue: z.array(analyticsQueueSampleSchema),
  skips: z.array(z.object({ category: skipCategorySchema, count: z.number() })), tiers: z.array(z.object({ tierIndex: z.number(), count: z.number() })),
  locations: z.array(analyticsLocationRowSchema),
});
export const adminJobRowSchema = z.object({
  id: z.string(), type: z.string(), payload: z.record(z.string(), z.unknown()), status: jobStatusSchema, attempts: z.number(), runAfter: iso,
  lastError: z.string().nullable(), dedupeKey: z.string().nullable(), result: z.unknown().nullable(), createdAt: iso, startedAt: iso.nullable(), finishedAt: iso.nullable(),
});
export const adminJobsResponseSchema = z.object({ jobs: z.array(adminJobRowSchema) });
export const adminEventsResponseSchema = z.object({ events: z.array(adminEventSchema.extend({ dedupeKey: z.string(), payload: z.unknown() })) });
export const ownerLinkSchema = z.object({ id: z.string(), ownerIdentifier: z.string(), kind: z.enum(["auto", "manual", "revoked"]) });
export const adminUserSchema = z.object({ id: z.string(), email: z.string(), name: z.string().nullable(), lastLoginAt: iso.nullable(), links: z.array(ownerLinkSchema) });
export const adminUsersResponseSchema = z.object({ users: z.array(adminUserSchema) });
export const adminAccountSchema = z.object({ email: z.string(), name: z.string().nullable(), lastLoginAt: iso.nullable() });
export const adminsResponseSchema = z.object({
  superAdmins: z.array(adminAccountSchema),
  admins: z.array(adminAccountSchema.extend({ grantedBy: z.string(), grantedAt: iso })),
  candidates: z.array(adminAccountSchema),
});
export const grantAdminResponseSchema = z.object({ granted: z.boolean(), reason: z.literal("super_admin").optional() });
export const apiClientSchema = z.object({
  id: z.string(), name: z.string(), keyPrefix: z.string(), locationIds: z.array(z.string()).nullable(),
  createdBy: z.string(), createdAt: iso, lastUsedAt: iso.nullable(), revokedAt: iso.nullable(),
});
export const apiClientsResponseSchema = z.object({ clients: z.array(apiClientSchema) });
/** `key` is the only time the full key is ever returned. */
export const apiClientKeyResponseSchema = z.object({ client: apiClientSchema, key: z.string() });
export type ApiClient = z.infer<typeof apiClientSchema>;
export type ApiClientKeyResponse = z.infer<typeof apiClientKeyResponseSchema>;
export const ownerLinkResponseSchema = z.object({ link: ownerLinkSchema.extend({ userId: z.string(), createdBy: z.string().nullable(), createdAt: iso }) });
export const directoryLocationSchema = z.object({
  blvdId: z.string(), name: z.string(), ownerIdentifier: z.string(), state: z.string().nullable(), timezone: z.string(),
  contactEmailNormalized: z.string().nullable(), syncedAt: iso, linkedUsers: z.array(z.string()),
});
export const adminDirectoryResponseSchema = z.object({ locations: z.array(directoryLocationSchema) });
export const bulkResponseSchema = z.object({ updated: z.number(), backfillJobs: z.number(), removalJobs: z.number(), replanJobs: z.number(), webhookJobs: z.number() });
export const webhookSchema = z.object({ id: z.string(), name: z.string().nullable(), url: z.string(), subscriptions: z.array(z.object({ id: z.string(), eventType: z.string(), enabled: z.boolean() })) });
export const webhookKindSchema = z.enum(["current", "legacy", "disabled", "other"]);
export const listedWebhookSchema = webhookSchema.extend({ kind: webhookKindSchema });
export const webhooksResponseSchema = z.object({ webhooks: z.array(listedWebhookSchema) });
export const webhookCreateResponseSchema = z.object({ created: z.array(z.string()), upgraded: z.array(z.string()) });

/** `blvdId` null = excluded at every location (admins only). `editable`: this session may remove it. */
export const staffExclusionSchema = z.object({
  id: z.string(), staffId: z.string(), staffName: z.string(), blvdId: z.string().nullable(), locationName: z.string().nullable(),
  reason: z.string().nullable(), createdBy: z.string(), createdAt: iso, editable: z.boolean(),
});
export const exclusionsResponseSchema = z.object({ exclusions: z.array(staffExclusionSchema), excludedRoles: z.array(z.string()) });
export const staffCandidateSchema = z.object({ staffId: z.string(), name: z.string(), role: z.string().nullable(), autoExcluded: z.boolean() });
export const staffListResponseSchema = z.object({ staff: z.array(staffCandidateSchema) });
export const createExclusionResponseSchema = z.object({ created: z.boolean(), followUpJobs: z.number() });
export const removeExclusionResponseSchema = z.object({ removed: z.boolean(), followUpJobs: z.number() });

export type Mode = z.infer<typeof modeSchema>;
export type Role = z.infer<typeof roleSchema>;
export type Edge = z.infer<typeof edgeSchema>;
export type ActionType = z.infer<typeof actionTypeSchema>;
export type Me = z.infer<typeof meSchema>;
export type ApiErrorBody = z.infer<typeof apiErrorSchema>;
export type LocationSummary = z.infer<typeof locationSummarySchema>;
export type Tier = z.infer<typeof tierSchema>;
export type RuleSet = z.infer<typeof ruleSetSchema>;
export type ResolvedRuleSet = z.infer<typeof resolvedRuleSetSchema>;
export type RulesResponse = z.infer<typeof rulesResponseSchema>;
export type RulesHoursResponse = z.infer<typeof rulesHoursResponseSchema>;
export type ActionRow = z.infer<typeof actionRowSchema>;
export type JobResponse = z.infer<typeof jobResponseSchema>;
export type PreviewResult = z.infer<typeof previewResultSchema>;
export type PreviewAction = PreviewResult["actions"][number];
export type AdminSystem = z.infer<typeof adminSystemSchema>;
export type AdminJobRow = z.infer<typeof adminJobRowSchema>;
export type AdminEvent = z.infer<typeof adminEventSchema>;
export type AdminUser = z.infer<typeof adminUserSchema>;
export type DirectoryLocation = z.infer<typeof directoryLocationSchema>;
export type Webhook = z.infer<typeof webhookSchema>;
export type ListedWebhook = z.infer<typeof listedWebhookSchema>;
export type StaffExclusion = z.infer<typeof staffExclusionSchema>;
export type StaffCandidate = z.infer<typeof staffCandidateSchema>;
export type AdminAnalytics = z.infer<typeof adminAnalyticsSchema>;
export type AnalyticsTotals = z.infer<typeof analyticsTotalsSchema>;
export type AnalyticsDailyRow = z.infer<typeof analyticsDailyRowSchema>;
export type AnalyticsQueueSample = z.infer<typeof analyticsQueueSampleSchema>;
export type AnalyticsLocationRow = z.infer<typeof analyticsLocationRowSchema>;
export type AnalyticsJobBucket = z.infer<typeof analyticsJobBucketSchema>;
export type SkipCategory = z.infer<typeof skipCategorySchema>;

export const notReleasableSchema = z.enum(["shadow_mode", "location_disabled", "location_paused", "globally_paused", "start_locked", "end_locked"]);
export const partnerBlockSchema = z.object({
  blockId: z.string(), staffId: z.string(), staffName: z.string(), edge: edgeSchema, startAt: iso, endAt: iso,
  releasable: z.boolean(), notReleasableReason: notReleasableSchema.nullable(),
});
export const partnerBlocksResponseSchema = z.object({ locationId: z.string(), mode: modeSchema, blocks: z.array(partnerBlockSchema) });
export const partnerLocationsResponseSchema = z.object({ locations: z.array(z.object({ locationId: z.string(), name: z.string(), timezone: z.string(), aiBookingEnabled: z.boolean() })) });
export const partnerReleaseResponseSchema = z.object({ released: z.literal(true), blockId: z.string(), holdUntil: iso });
export const partnerErrorSchema = z.object({ error: z.string(), message: z.string(), released: z.literal(false).optional(), reason: z.string().optional() });

// Owner Results page (`src/results/types.ts` holds the TypeScript shapes these mirror).
const dateWindowSchema = z.object({ from: day, to: day, days: z.number() });
const growthSchema = z.object({ mode: z.enum(["yoy", "before_only"]), beforePct: z.number(), sincePct: z.number() });
const metricWindowsSchema = z.object({ since: z.number(), before: z.number(), sinceLastYear: z.number().nullable(), beforeLastYear: z.number().nullable() });
const landedSchema = z.object({ first: z.number(), middle: z.number(), last: z.number(), matched: z.number() });
export const resultVerdictSchema = z.discriminatedUnion("state", [
  z.object({ state: z.literal("too_early"), daysLive: z.number() }),
  z.object({ state: z.literal("no_history") }),
  z.object({ state: z.literal("steady"), bookings: growthSchema.nullable() }),
  z.object({ state: z.literal("down"), metric: z.enum(["bookings", "serviceSales", "newClients"]), growth: growthSchema, bookings: growthSchema.nullable() }),
]);
const payWindowSchema = z.object({
  window: dateWindowSchema,
  months: z.array(z.object({ staffId: z.string(), month: z.string(), minutes: z.number(), commissionCents: z.number() })),
  weeks: z.array(z.object({ from: day, minutes: z.number(), commissionCents: z.number() })),
});
export const paySnapshotSchema = z.object({
  before: payWindowSchema, since: payWindowSchema,
  staff: z.array(z.object({ staffId: z.string(), name: z.string() })),
  unclassifiedCents: z.number(), salesCents: z.number(),
});
export const locationResultsSchema = z.object({
  blvdId: z.string(), goLive: day, daysLive: z.number(),
  windows: z.object({ since: dateWindowSchema, before: dateWindowSchema, sinceLastYear: dateWindowSchema, beforeLastYear: dateWindowSchema }),
  metrics: z.object({ bookings: metricWindowsSchema, serviceSales: metricWindowsSchema, newClients: metricWindowsSchema }),
  landed: z.object({ before: landedSchema, since: landedSchema, basis: z.enum(["shift", "opening_hours"]) }).nullable(),
  verdict: resultVerdictSchema, computedAt: iso,
  pay: paySnapshotSchema.nullable().optional(),
});
const blockedTotalsSchema = z.object({ blockedMinutes: z.number(), openedMinutes: z.number() });
/** Owner pay settings: minimum wage in cents ($1–$50) and the fallback commission rate in basis points (5%–40%); null = not set. */
export const paySettingsSchema = z.object({
  minWageCents: z.number().int().min(100).max(5000).nullable(),
  fallbackCommissionBp: z.number().int().min(500).max(4000).nullable(),
});
export type PaySettings = z.infer<typeof paySettingsSchema>;
export const locationResultsRowSchema = z.object({
  blvdId: z.string(), name: z.string(), timezone: z.string(), goLive: day,
  results: locationResultsSchema.nullable(),
  pay: paySettingsSchema,
  blocked: z.object({ d30: blockedTotalsSchema, d60: blockedTotalsSchema, d90: blockedTotalsSchema, lifetime: blockedTotalsSchema }),
});
export const resultsResponseSchema = z.object({ locations: z.array(locationResultsRowSchema) });
export type LocationResultsRow = z.infer<typeof locationResultsRowSchema>;
